import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  forwardRef,
  Input,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { AuthState } from '@wilson/auth/core';
import { OrganizationalUnit } from '@wilson/interfaces';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { map, Observable } from 'rxjs';

@Component({
  selector: 'wilson-organizational-unit-select',
  imports: [CommonModule, TranslatePipe, NzSelectModule, FormsModule],
  templateUrl: './organizational-unit-select.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => OrganizationalUnitSelectComponent),
      multi: true,
    },
  ],
})
export class OrganizationalUnitSelectComponent implements ControlValueAccessor {
  private readonly store = inject(Store);
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() organizationalUnits$: Observable<OrganizationalUnit[]> = this.store
    .select(AuthState.accessibleOrgUnits)
    .pipe(map((orgUnits) => orgUnits ?? []));
  @Input() placeholder = '';

  protected disabled = false;
  value = '';

  onChange?: (value: string) => void;
  onTouched?: () => void;

  writeValue(value: string): void {
    this.value = value;
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.detectChanges();
  }

  onSelect(orgUnitId: string) {
    this.value = orgUnitId;
    this.onChange?.(orgUnitId);
    this.onTouched?.();
  }

  compareOrgUnitFn = (o1: string, o2: string) => o1 === o2;
}
